import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
} from "react";

type RefreshFn = () => void | Promise<void>;

interface AdminUiValue {
  searchRef: RefObject<HTMLInputElement | null>;
  focusSearch: () => void;
  registerRefresh: (fn: RefreshFn | null) => void;
  triggerRefresh: () => void;
  shortcutsOpen: boolean;
  setShortcutsOpen: (open: boolean) => void;
}

const AdminUiContext = createContext<AdminUiValue | null>(null);

/** Cross-cutting admin UI wiring (global search focus, refresh hotkey, help). */
export function AdminUiProvider({ children }: { children: ReactNode }) {
  const searchRef = useRef<HTMLInputElement | null>(null);
  const refreshRef = useRef<RefreshFn | null>(null);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);

  const focusSearch = useCallback(() => searchRef.current?.focus(), []);
  const registerRefresh = useCallback((fn: RefreshFn | null) => {
    refreshRef.current = fn;
  }, []);
  const triggerRefresh = useCallback(() => {
    void refreshRef.current?.();
  }, []);

  const value = useMemo<AdminUiValue>(
    () => ({
      searchRef,
      focusSearch,
      registerRefresh,
      triggerRefresh,
      shortcutsOpen,
      setShortcutsOpen,
    }),
    [focusSearch, registerRefresh, triggerRefresh, shortcutsOpen],
  );

  return <AdminUiContext.Provider value={value}>{children}</AdminUiContext.Provider>;
}

export function useAdminUi(): AdminUiValue {
  const ctx = useContext(AdminUiContext);
  if (!ctx) throw new Error("useAdminUi must be used inside <AdminUiProvider>");
  return ctx;
}
